/**
 * About / Portfolio Page
 *
 * Provides:
 * - Professional portfolio overview
 * - Contact links (LinkedIn, Email)
 * - Professional Experience section
 * - Projects section
 * - Technical Skills section
 * - Education section
 * - Leadership section
 * - Back to login navigation
 */

import { Link } from 'react-router-dom';
import './About.css';

interface Experience {
  title: string;
  company: string;
  location: string;
  period: string;
  highlights: string[];
}

interface Project {
  name: string;
  period: string;
  description: string;
  highlights: string[];
}

interface SkillCategory {
  category: string;
  skills: string[];
}

const professionalExperience: Experience[] = [
    {
    title: 'Tech Project Manager',
    company: 'Pfizer',
    location: 'New York, NY',
    period: 'June 2025 - Present',
    highlights: [
      'In Progress',
    ],
  },
  {
    title: 'Associate Developer',
    company: 'Pfizer',
    location: 'New York, NY',
    period: 'June 2024 - June 2025',
    highlights: [
      'Advised on website brand migrations to Pfizer platforms, focusing on Adobe Franklin and Web Builder approaches, UI/UX, asset verification, and approach estimation.',
      'Launched key features on multiple websites, achieving 90%+ performance scores using HTML, CSS, and JavaScript, while efficiently managing pull requests with Git.',
      'Enhanced 20% of OSS service offerings and their corresponding end-to-end documentation process by utilizing an Adobe Franklin-based portal, resulting in a 12% increase in retention measured by Adobe Analytics.',
    ],
  },
  {
    title: 'Omnichannel Services Solutions Intern',
    company: 'Pfizer',
    location: 'Peapack, NJ',
    period: 'June 2023 - May 2024',
    highlights: [
      'Automated retrieval and analysis of Adobe Analytics and Google Maps APIs data using Python, Adobe API, OAuth, and BigQuery, reducing human error from 20% to 0% and extracting detailed billing insights for efficient expenditure tracking.',
      'Remediated data discrepancies in Adobe Analytics impacting 12 renowned brands\' campaign performance, ensuring accuracy for 14 critical KPIs utilizing Tableau dashboards within Jira-managed Scrum sprints.',
      'Consolidated the Webpack configuration for 28 JavaScript files into 2 production and development bundles, reducing HTTP requests and enhancing load times.',
      'Streamlined Analytics support for over 600 users by migrating 50+ articles to a single, user-friendly platform built with Markdown, Hugo, and Git.',
    ],
  },
  {
    title: 'Software Engineer Intern',
    company: 'VNG Cloud',
    location: 'Ho Chi Minh City, Vietnam',
    period: 'July 2022 - August 2022',
    highlights: [
      'Optimized access management for VNG Cloud\'s IAM employees by architecting an internal feature built with TypeScript and Angular.',
      'Enhanced user experience and data accessibility by building a comprehensive search feature into an existing Angular application and REST backend, enabling users to locate information across multiple data sources efficiently.',
      'Refined the workflow for 20 design and development team members by translating interactive Figma prototypes into production-ready features.',
    ],
  },
];

const projects: Project[] = [
  {
    name: 'Jasmin',
    period: 'September 2023 - May 2024',
    description: 'A user-friendly mental health platform providing seamless telehealth services.',
    highlights: [
      'Collaboratively designed mental health platform using Node.js, Express.js, MongoDB, React, Firebase, and Zoom API to connect 80+ patients with psychologists.',
      'Integrated restricted AI chatbot powered by ChatGPT to assist users in finding appropriate mental health professionals and offer recommendations.',
      'Implemented real-time chat, appointment scheduling, personalized profiles, and feedback system for patients and professionals, ensuring high quality assurance and average psychologist rating of 4.3/5.',
    ],
  },
  {
    name: 'TRVL',
    period: 'January 2023 - May 2023',
    description: 'A travel app allowing users to plan their trips efficiently and virtually explore 500+ destinations before booking.',
    highlights: [
      'Co-engineered travel app with React, Redis, Elasticsearch, Git, and Node.js, transforming efficiency by optimizing Yelp API queries and strategically indexing user comments to reduce query time by 80%.',
      'Deployed a high-impact Redis cache with a remarkable 25% hit rate, leading to an exceptional average 91% reduction in page load time.',
    ],
  },
];

const technicalSkills: SkillCategory[] = [
  {
    category: 'Programming Languages',
    skills: ['JavaScript', 'Python', 'Java', 'C++', 'C'],
  },
  {
    category: 'Backend Technologies',
    skills: ['Node.js', 'Express.js', 'MongoDB', 'Elasticsearch', 'SQL (MySQL, BigQuery)', 'Redis', 'JSON'],
  },
  {
    category: 'Frontend Technologies',
    skills: ['HTML', 'CSS', 'React', 'Angular', 'Handlebars.js', 'TypeScript'],
  },
  {
    category: 'Version Control & Collaboration',
    skills: ['Git', 'Jira', 'SharePoint', 'Markdown'],
  },
  {
    category: 'Development Tools',
    skills: ['Visual Studio Code', 'Linux', 'Ubuntu', 'Resource Override', 'Omnibug'],
  },
  {
    category: 'Data & Analytics',
    skills: ['Tableau', 'Adobe Analytics', 'Excel'],
  },
  {
    category: 'Software',
    skills: ['PowerApps', 'OAuth', 'Webpack'],
  },
];

const leadership: string[] = [
  'Ethnic Student Council Treasurer (2022 - 2023)',
  'Filipino Association Internal Rep (2021)',
  'Society of Asian Scientists and Engineers',
  'Chinese Student Association',
];

export function About(): JSX.Element {
  return (
    <div className="about">
      <header className="about__header">
        <Link to="/login" className="about__back-button">
          Back to Login
        </Link>
      </header>

      <main className="about__content">
        <section className="about__hero">
          <div className="about__avatar">
            <span className="about__avatar-text">DT</span>
          </div>
          <h1 className="about__name">Dylan Tran</h1>
          <p className="about__tagline">Tech Project Manager</p>

          <p className="about__bio">
            I'm a Software Engineer and Tech Project Manager, passionate about getting things done on time and building user-friendly applications. This site is my personal playground, and over time I would like to store book reviews, recipes, or anything interesting.
          </p>

          <blockquote className="about__quote">
            Whatever happens, happens.
          </blockquote>

          <div className="about__contact">
            <a
              href="https://www.linkedin.com/in/dylan-tran-cs/"
              target="_blank"
              rel="noopener noreferrer"
              className="about__contact-link about__contact-link--linkedin"
            >
              <span className="about__contact-icon">in</span>
              LinkedIn
            </a>
            <a
              href="mailto:dylan.tran.cs@gmail.com"
              className="about__contact-link about__contact-link--email"
            >
              <span className="about__contact-icon">@</span>
              Email
            </a>
          </div>
        </section>

        <section className="about__section">
          <h2 className="about__section-title">Professional Experience</h2>
          <div className="about__experience-list">
            {professionalExperience.map((exp, index) => (
              <article key={index} className="about__experience-card">
                <div className="about__experience-header">
                  <h3 className="about__experience-title">{exp.title}</h3>
                  <span className="about__experience-period">{exp.period}</span>
                </div>
                <p className="about__experience-company">
                  {exp.company} | {exp.location}
                </p>
                <ul className="about__experience-highlights">
                  {exp.highlights.map((highlight, hIndex) => (
                    <li key={hIndex}>{highlight}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="about__section">
          <h2 className="about__section-title">Projects</h2>
          <div className="about__experience-list">
            {projects.map((project, index) => (
              <article key={index} className="about__experience-card">
                <div className="about__experience-header">
                  <h3 className="about__experience-title">{project.name}</h3>
                  <span className="about__experience-period">{project.period}</span>
                </div>
                <p className="about__project-tagline">{project.description}</p>
                <ul className="about__experience-highlights">
                  {project.highlights.map((highlight, hIndex) => (
                    <li key={hIndex}>{highlight}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="about__section">
          <h2 className="about__section-title">Technical Skills</h2>
          <div className="about__skills-grid">
            {technicalSkills.map((category, index) => (
              <div key={index} className="about__skill-category">
                <h3 className="about__skill-category-title">{category.category}</h3>
                <div className="about__skill-tags">
                  {category.skills.map((skill, sIndex) => (
                    <span key={sIndex} className="about__tech-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about__section">
          <h2 className="about__section-title">Education</h2>
          <div className="about__education-card">
            <h3 className="about__education-degree">Bachelor of Science in Computer Science</h3>
            <p className="about__education-minor">Minor in Cybersecurity</p>
            <p className="about__education-minor">Stevens Institute of Technology</p>
          </div>
        </section>

        <section className="about__section">
          <h2 className="about__section-title">Leadership & Community Involvement</h2>
          <div className="about__leadership-list">
            {leadership.map((item, index) => (
              <span key={index} className="about__leadership-item">
                {item}
              </span>
            ))}
          </div>
        </section>

        <footer className="about__footer">
          <Link to="/login" className="about__cta-button">
            Back to Login
          </Link>
        </footer>
      </main>
    </div>
  );
}
